import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { StatusCode } from '../../../components/Alert.tsx';
import { Form, FormActions, FormFile, FormInput, FormSelect, FormSwitch, FormTextArea } from '../../../components/form/index.js';
import Image from '../../../components/Image.tsx';
import NutritionalValuesComponent from '../../../components/NutritionalValues.tsx';
import ScanBarcode from '../../../components/search/ScanBarcode.tsx';
import { useAlert } from '../../../contexts/alertProvider.tsx';
import { useAlimentContext } from '../../../contexts/aliments/aliment.tsx';
import { useUsersContext } from '../../../contexts/users.tsx';
import { Aliment } from '../../../models/aliments/aliment.ts';
import { NutritionalValues } from '../../../models/aliments/nutritionalValues.ts';
import { Role } from '../../../models/enums/role.ts';
import formatNumber from '../../../models/helpers/formatNumber.ts';
const MAX_FILE_SIZE = 1024 * 1024;//1MB

const validationSchema = Yup.object().shape({
  name: Yup
    .string()
    .required('Name is required')
    .min(2, "Name is too short")
    .max(255, 'Name is too long'),
  brand: Yup
    .string()
    .max(255, 'Brand is too long'),
  measurementUnitID: Yup
    .number()
    .required('Measurement unit is required'),
  categoryID: Yup
    .number()
    .required('Category is required'),
  defaultQuantity: Yup
    .number()
    .nullable()
    .typeError('Quantity must be a number')
    .min(0, 'Quantity must be a positive number'),
  description: Yup.string().nullable(),
  barCode: Yup.number().nullable(),
  isPublished: Yup.boolean(),
  isValidated: Yup.boolean(),
  alimentStores: Yup.array()
    .of(
      Yup.object().shape({
        storeID: Yup.number()
          .required('Store ID is required')
          .typeError('Store ID must be a number')
      })
    )
    .nullable()
});

const validationNutritionalValuesSchema = Yup.object().shape({
  calories: Yup
    .number()
    .required('Calories required')
    .typeError('Calories must be a number')
    .min(0, "Calories must be a positive number"),
  totalFat: Yup
    .number()
    .required('Total fat required')
    .typeError('Total fat must be a number')
    .min(0, "Total fat must be a positive number"),
  saturatedFat: Yup
    .number()
    .required('Saturated fat required')
    .typeError('Saturated fat must be a number')
    .min(0, "Saturated fat must be a positive number"),
  unsaturatedFat: Yup
    .number()
    .nullable()
    .typeError('Unsaturated fat must be a number')
    .min(0, "Unsaturated fat must be a positive number"),
  carbohydrates: Yup
    .number()
    .required('Carbohydrates required')
    .typeError('Carbohydrates must be a number')
    .min(0, "Carbohydrates must be a positive number"),
  dietarySugar: Yup
    .number()
    .required('Dietary sugar required')
    .typeError('Dietary sugar must be a number')
    .min(0, "Dietary sugar must be a positive number"),
  fibre: Yup
    .number()
    .required('Fibre required')
    .typeError('Fibre must be a number')
    .min(0, "Fibre must be a positive number"),
  protein: Yup
    .number()
    .required('Protein required')
    .typeError('Protein must be a number')
    .min(0, "Protein must be a positive number"),
  salt: Yup
    .number()
    .required('Salt required')
    .typeError('Salt must be a number')
    .min(0, "Salt must be a positive number")
});

const AlimentBody = () => {
  const { id } = useParams(); // Get the 'id' from the route parameter
  const userContext = useUsersContext();
  const {
    measurementUnits,
    categories,
    stores,
  } = useUsersContext();
  const userIsAdmin = userContext.isInRole(Role.admin) || userContext.isInRole(Role.superAdmin);
  const showIsValidated = userIsAdmin//TODO: || userContext.canValidate;
  const {
    isLoaded,
    aliment,
    insertAliment,
    updateAliment } = useAlimentContext();
  const [localAliment, setLocalAliment] = useState<Aliment>(aliment);
  const [localNutritionalValues, setLocalNutritionalValues] = useState<NutritionalValues>(aliment.nutritionalValues ?? {} as NutritionalValues);
  useEffect(() => {
    setLocalAliment(aliment);
    if (aliment.nutritionalValues)
      setLocalNutritionalValues(aliment.nutritionalValues);
  }, [aliment])
  const [canEdit, setCanEdit] = useState(userIsAdmin || aliment.userID === 0 || aliment.userID === userContext.userID);
  useEffect(() => {
    setCanEdit(userIsAdmin || aliment.userID == 0 || aliment.userID == userContext.userID);
  }, [userIsAdmin, aliment, userContext.userID])
  const [showScanBarcode, setShowScanBarcode] = useState<boolean>(false);
  const [onlyBarCodeChange, setOnlyBarCodeChange] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [fileInputValue, setFileInputValue] = useState<any>('');
  const { showAlert } = useAlert();
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: any) => {
    const { id, value } = e.target;

    setLocalAliment((prevData: Aliment) => ({
      ...prevData,
      [id]: value ?? null, // Update the corresponding field in state
    }));

    validationSchema
      .validateAt(id, { ...localAliment, [id]: value ?? null })
      .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
      .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files, value } = e.target;
    if (!files || files.length === 0) {
      setFile(null);
      showAlert(StatusCode.warning, "No file selected");
      return;
    }

    const selectedFile = files[0];
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFile(null);
      setFileInputValue(''); // reset input value
      showAlert(
        StatusCode.error,
        `File size too large. Maximum allowed size is ${MAX_FILE_SIZE / (1024 * 1024)} MB`
      );
      return;
    }

    setFile(selectedFile);
    setFileInputValue(value);
  };

  const handleScanBarCodeResult = (result: string) => {
    if (!canEdit)
      setOnlyBarCodeChange(true);

    setLocalAliment((prevData: Aliment) => ({
      ...prevData,
      barCode: result ?? null
    }));
  }

  const handleStoresChange = (e: any) => {
    const { id, options } = e.target;

    // Extract all selected values (for multi-select)
    const selectedValues = Array.from(options)
      .filter((option: any) => option.selected)
      .map((option: any) => aliment?.alimentStores.find(a => a.storeID == option.value) ?? { storeID: option.value });

    setLocalAliment((prevData: Aliment) => ({
      ...prevData,
      [id]: selectedValues, // Update the corresponding field in state
    }));

    validationSchema
      .validateAt(id, { ...localAliment, [id]: selectedValues })
      .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
      .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
  };

  const handleNutritionalValuesInputChange = (e: any) => {
    const { id, value } = e.target;
    var formattedValue = formatNumber.FormatStringNumber(value);

    setLocalNutritionalValues((prevData: NutritionalValues) => ({
      ...prevData,
      [id]: formattedValue, // Update the corresponding field in state
    }));

    setLocalAliment((prevData: Aliment) => ({
      ...prevData,
      nutritionalValues: localNutritionalValues
    }))

    validationNutritionalValuesSchema
      .validateAt(id, { ...localNutritionalValues, [id]: value })
      .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
      .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
  };

  const handleSubmit = () => {
    validationSchema
      .validate(localAliment, { abortEarly: false })
      .then(() => {
        validationNutritionalValuesSchema
          .validate(localNutritionalValues, { abortEarly: false })
          .then(() => {
            setLocalAliment(localAliment);
            if (id == "create")
              insertAliment(localAliment, file);
            else
              updateAliment(localAliment, file);
          })
          .catch((err) => {
            const validationErrors: Record<string, string> = {};
            err.inner.forEach((error: Yup.ValidationError) => {
              if (error.path) validationErrors[error.path] = error.message;
            });
            setErrors(validationErrors);
          });
      })
      .catch((err) => {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      });
  };

  if (!isLoaded) return;
  return (
    <Form>
      {id != undefined && id != "create" &&
        <div className="w-100 d-flex justify-content-center mb-3">
          <Image
            entityType="Aliment"
            entityID={parseInt(id)}
            className="w-auto h-10rem"
          />
        </div>
      }
      <FormInput
        entity={localAliment}
        handleInputChange={handleInputChange}
        fieldName='name'
        label='Name'
        disabled={!canEdit}
        errors={errors} />
      <FormInput
        entity={localAliment}
        handleInputChange={handleInputChange}
        fieldName='brand'
        label='Brand'
        disabled={!canEdit}
        errors={errors} />
      <FormSelect
        entity={localAliment}
        handleInputChange={handleInputChange}
        options={measurementUnits}
        fieldName='measurementUnitID'
        label='Measurement unit'
        disabled={!canEdit}
        errors={errors} />
      <FormSelect
        entity={localAliment}
        handleInputChange={handleInputChange}
        options={categories}
        fieldName='categoryID'
        label='Category'
        disabled={!canEdit}
        errors={errors} />
      <FormSelect
        entity={localAliment}
        handleInputChange={handleStoresChange}
        options={stores}
        fieldName='alimentStores'
        multipleFieldName='storeID'
        label='Stores'
        multiple
        disabled={!canEdit}
        errors={errors} />
      <NutritionalValuesComponent
        nutritionalValues={localNutritionalValues}
        handleInputChange={handleNutritionalValuesInputChange}
        canEdit={canEdit}
        measurementUnit={measurementUnits.find(x => x.measurementUnitID == localAliment.measurementUnitID)?.name}
        errors={errors}
      />
      <FormInput
        entity={localAliment}
        handleInputChange={handleInputChange}
        fieldName='defaultQuantity'
        label='Default Quantity'
        type="numeric"
        append={measurementUnits.find(x => x.measurementUnitID == localAliment.measurementUnitID)?.name}
        disabled={!canEdit}
        errors={errors} />
      <FormTextArea
        entity={localAliment}
        handleInputChange={handleInputChange}
        fieldName='description'
        label='Description'
        disabled={!canEdit}
        errors={errors} />
      {canEdit &&
        <FormFile
          fileInputValue={fileInputValue}
          fieldName='Photo'
          handleFileChange={handleFileChange}
        />}
      {userContext.isLoggedIn
        ? showScanBarcode
          ? <ScanBarcode handleResult={handleScanBarCodeResult} setShowScanBarcode={setShowScanBarcode} />
          : <FormInput
            entity={localAliment}
            handleInputChange={handleInputChange}
            fieldName='barCode'
            label='BarCode'
            type="numeric"
            append={<i className="bi bi-upc-scan" />}
            handleAppendOnClick={() => setShowScanBarcode(true)}
            disabled={!canEdit}
          />
        : undefined
      }
      {canEdit &&
        <FormSwitch
          entity={localAliment}
          handleInputChange={handleInputChange}
          fieldName='isPublished'
          label='Publish aliment'
        />
      }
      {showIsValidated &&
        <FormSwitch
          entity={localAliment}
          handleInputChange={handleInputChange}
          fieldName='isValidated'
          label='Validate aliment'
        />
      }
      {(canEdit || (!canEdit && onlyBarCodeChange)) &&
        <FormActions
          mainAction={handleSubmit}
          mainActionText="Save"
          showCancelButton
        />
      }
      <small className="text-muted">
        Cooked values (e.g., boiled or baked) will differ slightly due to water gain or loss.
      </small>
    </Form>
  );
};

export default AlimentBody;
